import React from "react";
import {
  BlockIcon,
  ArrowIcon,
  EditIcon,
  DeleteIcon,
  DownloadIcon,
} from "assets";
import { FEATURE_FLAGS, useLaunchDarklyFlags } from "shared-services";
import { IControlsContainerContent } from "./ControlsContainerContent";
import { ButtonPanel } from "mediaModules/media/components/ButtonPanel";

import { MediaAsset, TranscodeStatus } from "mediaModules/media/models";
import { IActionButtonProps } from "component-library/src/components/Atoms/ActionButton/ActionButton";
import { heapTrackingForControlsContainer } from "app/helpers/heapTracking";
import "./ControlsContainer.scss";

export enum ControlIcons {
  moveUp = "arrowup",
  moveDown = "arrowdown",
  edit = "edit",
  delete = "delete",
  hide = "hide",
  download = "download1",
}

export enum DirectionType {
  up = "up",
  down = "down",
}

export interface IControlsConfig {
  isMultipleMode: boolean;
  hasBrokenItem: boolean;
  hasProcessingItem: boolean;
}

export interface IControlsContainerProps {
  isBlocked: boolean;
  content: IControlsContainerContent;
  hideShowreel: () => void;
  moveShowreel: (direction: DirectionType) => void;
  deleteShowreel: () => void;
  editShowreel: () => void;
  downloadShowreel: () => void;
  selectedShowreels: MediaAsset[];
}

export const ControlsContainer: React.FC<
  React.PropsWithChildren<IControlsContainerProps>
> = (props) => {
  const isProfileRedesignEnabled = useLaunchDarklyFlags(
    FEATURE_FLAGS.ProfileRedesign
  );

  const {
    content,
    isBlocked,
    hideShowreel,
    moveShowreel,
    deleteShowreel,
    editShowreel,
    downloadShowreel,
    selectedShowreels,
  } = props;

  const { isMultipleMode, hasBrokenItem, hasProcessingItem } =
    selectedShowreels.reduce(
      (config: IControlsConfig, { transcodeStatus }) => {
        if (transcodeStatus === TranscodeStatus.Error) {
          config.hasBrokenItem = true;
        }

        if (
          transcodeStatus === TranscodeStatus.Processing ||
          transcodeStatus === TranscodeStatus.Awaiting
        ) {
          config.hasProcessingItem = true;
        }

        return config;
      },
      {
        isMultipleMode: selectedShowreels.length > 1,
        hasBrokenItem: false,
        hasProcessingItem: false,
      }
    );

  const isControlDisabled = (type: ControlIcons) => {
    if (!selectedShowreels.length || isBlocked) {
      return true;
    }

    if (type !== ControlIcons.delete && hasProcessingItem) {
      return true;
    }

    if (
      type === ControlIcons.moveUp ||
      type === ControlIcons.moveDown ||
      type === ControlIcons.edit ||
      type === ControlIcons.download
    ) {
      return isMultipleMode || hasBrokenItem;
    }

    return false;
  };

  const buttons: (IActionButtonProps & { key: string | number })[] = [
    {
      key: ControlIcons.moveUp,
      svgIcon: <ArrowIcon />,
      label: content.moveUp,
      onClick: () => {
        heapTrackingForControlsContainer(isProfileRedesignEnabled);

        moveShowreel(DirectionType.up);
      },
      disabled: isControlDisabled(ControlIcons.moveUp),
    },
    {
      key: ControlIcons.moveDown,
      svgIcon: <ArrowIcon className="rotate-180" />,
      label: content.moveDown,
      onClick: () => {
        heapTrackingForControlsContainer(isProfileRedesignEnabled);

        moveShowreel(DirectionType.down);
      },
      disabled: isControlDisabled(ControlIcons.moveDown),
    },
    {
      key: ControlIcons.edit,
      svgIcon: <EditIcon />,
      label: content.edit,
      onClick: () => {
        heapTrackingForControlsContainer(isProfileRedesignEnabled);

        editShowreel();
      },
      disabled: isControlDisabled(ControlIcons.edit),
    },
    {
      key: ControlIcons.delete,
      svgIcon: <DeleteIcon />,
      label: content.delete,
      onClick: () => {
        heapTrackingForControlsContainer(isProfileRedesignEnabled);

        deleteShowreel();
      },
      disabled: isControlDisabled(ControlIcons.delete),
    },
    {
      key: ControlIcons.hide,
      svgIcon: <BlockIcon />,
      label: content.hide,
      onClick: () => {
        heapTrackingForControlsContainer(isProfileRedesignEnabled);

        hideShowreel();
      },
      disabled: isControlDisabled(ControlIcons.hide),
    },
    {
      key: ControlIcons.download,
      svgIcon: <DownloadIcon />,
      label: content.download,
      onClick: downloadShowreel,
      disabled: isControlDisabled(ControlIcons.download),
    },
  ];

  return (
    <div className="c-controls-container g-bg-secondary">
      <ButtonPanel buttons={buttons} />
    </div>
  );
};
